import { useState, useEffect } from "react";
import { CheckCircle2, ArrowRight, Kanban, FolderPlus, ListTodo, Sparkles, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";

const STEPS = [
  { id: "welcome", icon: Sparkles, title: "Welcome to Opero Smart!", desc: "Let's get you set up in 30 seconds. We'll walk you through the basics.", color: "text-primary" },
  { id: "project", icon: FolderPlus, title: "Create a Project", desc: "Projects help you organize related tasks. Click 'Projects' in the sidebar, then '+ New Project'.", color: "text-emerald-500" },
  { id: "task", icon: ListTodo, title: "Add Your First Task", desc: "Click '+ Task' at the top right, give it a title, and save. Drag it between columns to update status.", color: "text-blue-500" },
  { id: "explore", icon: Kanban, title: "You're All Set!", desc: "Explore the Dashboard, Calendar, Timeline, and AI Quick Add. Upgrade to Pro for unlimited features.", color: "text-amber-500" },
];

export const OnboardingModal = ({ isOpen, onClose, onNavigate }) => {
  const [step, setStep] = useState(0);

  if (!isOpen) return null;

  const current = STEPS[step];
  const isLast = step === STEPS.length - 1;
  const Icon = current.icon;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm" data-testid="onboarding-modal">
      <div className="bg-card border rounded-2xl shadow-2xl w-full max-w-md mx-4 overflow-hidden">
        {/* Progress */}
        <div className="flex gap-1 p-4 pb-0">
          {STEPS.map((_, i) => (
            <div key={i} className={`h-1 flex-1 rounded-full transition-all ${i <= step ? 'bg-primary' : 'bg-muted'}`} />
          ))}
        </div>

        <div className="p-6 text-center">
          <div className={`w-16 h-16 rounded-2xl ${current.color === 'text-primary' ? 'bg-primary/10' : current.color === 'text-emerald-500' ? 'bg-emerald-500/10' : current.color === 'text-blue-500' ? 'bg-blue-500/10' : 'bg-amber-500/10'} flex items-center justify-center mx-auto mb-5`}>
            <Icon className={`h-8 w-8 ${current.color}`} />
          </div>

          <Badge variant="outline" className="mb-3">Step {step + 1} of {STEPS.length}</Badge>
          <h3 className="text-xl font-bold mb-2">{current.title}</h3>
          <p className="text-sm text-muted-foreground leading-relaxed mb-6">{current.desc}</p>

          <div className="flex gap-3">
            {step === 0 ? (
              <Button variant="ghost" onClick={onClose} className="flex-1" data-testid="onboarding-skip">Skip Tour</Button>
            ) : (
              <Button variant="outline" onClick={() => setStep(s => s - 1)} className="flex-1">Back</Button>
            )}

            {isLast ? (
              <Button onClick={() => { onClose(); }} className="flex-1 bg-primary" data-testid="onboarding-finish">
                <CheckCircle2 className="h-4 w-4 mr-2" />Start Using Opero Smart
              </Button>
            ) : (
              <Button onClick={() => {
                if (step === 1) onNavigate('projects');
                if (step === 2) onNavigate('board');
                setStep(s => s + 1);
              }} className="flex-1" data-testid="onboarding-next">
                Next <ArrowRight className="h-4 w-4 ml-2" />
              </Button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};
